import { TransactionTypeData, ProcessedTypeData } from './TransactionTypeChart.types'

// Legacy transaction type labels used when the chart is rendered without an app-level formatter.
export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  achat: 'Achat',
  heritage: 'Héritage',
  donation: 'Donation',
  partage: 'Partage',
  cession: 'Cession',
  jugement: 'Jugement',
  adjudication: 'Adjudication',
  'heritage-partage': 'Héritage-Partage',
  echange: 'Échange',
  transfert: 'Transfert',
  others: 'Autres',
}

const getTransactionTypeLabel = (
  type: string,
  formatTransactionType?: (type: string) => string
): string =>
  formatTransactionType?.(type === 'others' ? 'other' : type) ||
  TRANSACTION_TYPE_LABELS[type] ||
  type

// Stable palette for the canonical transaction_step_name values returned by Elasticsearch.
export const TRANSACTION_TYPE_COLORS: Record<string, string> = {
  sale: '#1E88E5',
  transfer: '#7E57C2',
  gratuitous_transfer: '#00ACC1',
  inheritance: '#FB8C00',
  partition: '#E53935',
  cession: '#43A047',
  mixed: '#8D6E63',
  forced_sale: '#D81B60',
  judgment: '#5C6BC0',
  exchange: '#00897B',
  easement_right: '#6D4C41',
  corporate_restructuring: '#546E7A',
  marital_regime: '#AB47BC',
  administrative: '#F4511E',
  compensation: '#C0CA33',
  other: '#90A4AE',
  achat: '#0294df',
  heritage: '#8E8E93',
  donation: '#FF9500',
  partage: '#FF3B30',
  jugement: '#805AD5',
  adjudication: '#5856D6',
  'heritage-partage': '#AF52DE',
  echange: '#32ADE6',
  transfert: '#007AFF',
  others: '#C7C7CC',
}

export function processTransactionTypes(
  data?: TransactionTypeData,
  formatTransactionType?: (type: string) => string
): ProcessedTypeData[] {
  if (!data?.transaction_types?.buckets) {
    return []
  }

  const total = data.transaction_types.buckets.reduce((sum, bucket) => sum + bucket.doc_count, 0)

  // Sort by doc_count and take top 10
  const sortedBuckets = [...data.transaction_types.buckets].sort(
    (a, b) => b.doc_count - a.doc_count
  )

  const top10 = sortedBuckets.slice(0, 10)
  const others = sortedBuckets.slice(10)

  const processedData = top10.map((bucket) => ({
    name: getTransactionTypeLabel(bucket.key, formatTransactionType),
    value: bucket.doc_count,
    percentage: total > 0 ? (bucket.doc_count / total) * 100 : 0,
    color: TRANSACTION_TYPE_COLORS[bucket.key] || '#C7C7CC',
    key: bucket.key, // Keep original key for reference
  }))

  // Add "Others" category if there are more than 10 types
  if (others.length > 0) {
    const othersCount = others.reduce((sum, bucket) => sum + bucket.doc_count, 0)
    processedData.push({
      name: getTransactionTypeLabel('others', formatTransactionType),
      value: othersCount,
      percentage: total > 0 ? (othersCount / total) * 100 : 0,
      color: '#C7C7CC',
      key: 'others',
    })
  }

  return processedData
}

export function processTimeSeriesData(data?: TransactionTypeData) {
  if (!data?.transaction_types_over_time?.buckets) {
    return { dates: [], series: {} }
  }

  const dates: string[] = []
  const seriesMap: Record<string, number[]> = {}
  const typeCounts: Record<string, number> = {}

  // First pass: collect all dates and count total occurrences for each type
  data.transaction_types_over_time.buckets.forEach((timeBucket) => {
    dates.push(timeBucket.key_as_string || String(timeBucket.key))

    if (timeBucket.by_type?.buckets) {
      timeBucket.by_type.buckets.forEach((typeBucket) => {
        typeCounts[typeBucket.key] = (typeCounts[typeBucket.key] || 0) + typeBucket.doc_count
      })
    }
  })

  // Get top 10 types by total count
  const sortedTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])

  const top10Types = new Set(sortedTypes.slice(0, 10).map(([key]) => key))

  // Initialize series for top 10 types and "Others"
  top10Types.forEach((type) => {
    seriesMap[type] = new Array(dates.length).fill(0)
  })

  // Only add "Others" if there are more than 10 types
  if (sortedTypes.length > 10) {
    seriesMap['others'] = new Array(dates.length).fill(0)
  }

  // Second pass: populate the data
  data.transaction_types_over_time.buckets.forEach((timeBucket, dateIndex) => {
    if (timeBucket.by_type?.buckets) {
      timeBucket.by_type.buckets.forEach((typeBucket) => {
        if (top10Types.has(typeBucket.key)) {
          // Add to the specific type series
          seriesMap[typeBucket.key][dateIndex] = typeBucket.doc_count || 0
        } else if (seriesMap['others']) {
          // Add to "Others" category
          seriesMap['others'][dateIndex] =
            (seriesMap['others'][dateIndex] || 0) + (typeBucket.doc_count || 0)
        }
      })
    }
  })

  return { dates, series: seriesMap }
}

export function processValueData(
  data?: TransactionTypeData,
  formatTransactionType?: (type: string) => string
) {
  if (!data?.total_value_by_type?.buckets) {
    return []
  }

  // Sort by total value and take top 10
  const sortedBuckets = [...data.total_value_by_type.buckets].sort(
    (a, b) => (b.total_value?.value || 0) - (a.total_value?.value || 0)
  )

  const top10 = sortedBuckets.slice(0, 10)
  const others = sortedBuckets.slice(10)

  const processedData = top10.map((bucket) => ({
    name: getTransactionTypeLabel(bucket.key, formatTransactionType),
    totalValue: bucket.total_value?.value || 0,
    avgValue: bucket.avg_value?.value || 0,
    count: bucket.doc_count,
    color: TRANSACTION_TYPE_COLORS[bucket.key] || '#C7C7CC',
    key: bucket.key,
  }))

  // Add "Others" category if there are more than 10 types
  if (others.length > 0) {
    const othersTotalValue = others.reduce(
      (sum, bucket) => sum + (bucket.total_value?.value || 0),
      0
    )
    const othersCount = others.reduce((sum, bucket) => sum + bucket.doc_count, 0)
    const othersAvgValue = othersCount > 0 ? othersTotalValue / othersCount : 0

    processedData.push({
      name: getTransactionTypeLabel('others', formatTransactionType),
      totalValue: othersTotalValue,
      avgValue: othersAvgValue,
      count: othersCount,
      color: '#C7C7CC',
      key: 'others',
    })
  }

  return processedData
}

export function calculateKPIs(
  data?: TransactionTypeData,
  formatTransactionType?: (type: string) => string
) {
  if (!data) {
    return {
      totalTransactions: 0,
      dominantType: '',
      dominantPercentage: 0,
      avgTransactionValue: 0,
      totalVolume: 0,
    }
  }

  const types = processTransactionTypes(data, formatTransactionType)
  const values = processValueData(data, formatTransactionType)

  const totalTransactions = types.reduce((sum, t) => sum + t.value, 0)
  const dominantType = types[0]?.name || ''
  const dominantPercentage = types[0]?.percentage || 0

  const totalVolume = values.reduce((sum, v) => sum + v.totalValue, 0)
  const avgTransactionValue = totalTransactions > 0 ? totalVolume / totalTransactions : 0

  return {
    totalTransactions,
    dominantType,
    dominantPercentage,
    avgTransactionValue,
    totalVolume,
  }
}

export function formatNumber(value: number): string {
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`
  }
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

export function formatCurrency(value: number): string {
  if (value >= 1000000000) {
    return `CHF ${(value / 1000000000).toFixed(1)}B`
  }
  if (value >= 1000000) {
    return `CHF ${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `CHF ${(value / 1000).toFixed(0)}K`
  }
  return `CHF ${value.toLocaleString()}`
}

export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`
}

export function getChartOptions(
  type: 'donut' | 'area' | 'bar',
  data: any,
  formatTransactionType?: (type: string) => string
) {
  const baseOptions = {
    animation: true,
    animationDuration: 800,
    animationEasing: 'cubicOut' as const,
    backgroundColor: 'transparent',
  }

  switch (type) {
    case 'donut':
      return {
        ...baseOptions,
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)',
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: {
            fontSize: 12,
          },
        },
        series: [
          {
            name: 'Transaction Types',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold',
              },
            },
            labelLine: {
              show: false,
            },
            data: data.map((item: ProcessedTypeData) => ({
              key: item.key,
              value: item.value,
              name: item.name,
              itemStyle: {
                color: item.color,
              },
            })),
          },
        ],
      }

    case 'area':
      return {
        ...baseOptions,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985',
            },
          },
        },
        legend: {
          data: Object.keys(data.series).map((key) => {
            return getTransactionTypeLabel(key, formatTransactionType)
          }),
          top: 0,
        },
        dataZoom: [
          {
            type: 'slider',
            show: true,
            xAxisIndex: [0],
            start: 0,
            end: 100,
            height: 20,
            bottom: 5,
          },
          {
            type: 'inside',
            xAxisIndex: [0],
            start: 0,
            end: 100,
          },
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true,
        },
        xAxis: [
          {
            type: 'category',
            boundaryGap: false,
            data: data.dates,
            axisLabel: {
              formatter: (value: string) => {
                // Just return the year value as-is since it's already formatted as 'yyyy'
                return value
              },
            },
          },
        ],
        yAxis: [
          {
            type: 'value',
          },
        ],
        series: Object.entries(data.series).map(([key, values]) => ({
          name: getTransactionTypeLabel(key, formatTransactionType),
          type: 'line',
          stack: 'Total',
          areaStyle: {},
          emphasis: {
            focus: 'series',
          },
          smooth: true,
          data: values,
          itemStyle: {
            color: key === 'others' ? '#C7C7CC' : TRANSACTION_TYPE_COLORS[key] || '#C7C7CC',
          },
        })),
      }

    case 'bar':
      return {
        ...baseOptions,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const item = params[0]
            return `${item.name}<br/>Total: ${formatCurrency(item.data.totalValue)}<br/>Moyenne: ${formatCurrency(item.data.avgValue)}<br/>Nombre: ${formatNumber(item.data.count)}`
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: data.map((item: any) => item.name),
          axisLabel: {
            interval: 0,
            rotate: 45,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => formatNumber(value),
          },
        },
        series: [
          {
            name: 'Valeur Totale',
            type: 'bar',
            data: data.map((item: any) => ({
              value: item.totalValue,
              totalValue: item.totalValue,
              avgValue: item.avgValue,
              count: item.count,
              itemStyle: {
                color: item.color,
              },
            })),
          },
        ],
      }

    default:
      return baseOptions
  }
}
